import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { JobSharedAttachments } from "../components/JobSharedAttachments";
import {
  Card,
  ErrorPanel,
  Loading,
  PageHeader,
} from "../components/ResourceView";
import {
  operationsJobsApi,
  type DispatchJobTransportWorkspace,
  type DispatchTransportCrewDriver,
  type DispatchTransportLeg,
  type JobPhase,
  type OperationsJobDetail,
} from "../lib/operationsJobs";
import {
  operationalCoreApi,
  type CrewAssignment,
  type CrewAvailabilityItem,
  type StaffingWorkspace,
} from "../lib/operationalCore";
import { useRpcResource } from "../lib/useRpcResource";

function label(v: string | null | undefined) {
  return v ? v.replaceAll("_", " ") : "—";
}
function dt(v: string | null | undefined) {
  return v ? new Date(v).toLocaleString() : "—";
}
function localInput(v: string | null | undefined) {
  if (!v) return "";
  const d = new Date(v);
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 16);
}
function shifted(v: string | null | undefined, hours: number) {
  if (!v) return "";
  const d = new Date(v);
  d.setHours(d.getHours() + hours);
  return localInput(d.toISOString());
}
function seedStart(p: JobPhase) {
  return (
    localInput(p.scheduled_start || p.required_window_start) ||
    shifted(p.scheduled_end || p.required_window_end, -2)
  );
}
function seedEnd(p: JobPhase) {
  return (
    localInput(p.scheduled_end || p.required_window_end) ||
    shifted(p.scheduled_start || p.required_window_start, 2)
  );
}
function phaseLabel(p: JobPhase) {
  if (p.phase_code === "pickup_teardown" || p.phase_type === "teardown")
    return "Pickup / Teardown";
  if (p.phase_code === "receiving_inspection" || p.phase_type === "inspection")
    return "Receiving / Inspection";
  const names: Record<string, string> = {
    warehouse_prep: "Warehouse Prep",
    loading: "Loading",
    delivery: "Delivery",
    installation: "Installation",
    on_rent: "On Rent",
    return_transport: "Return Transport",
  };
  return names[p.phase_type] ?? label(p.phase_type);
}
function phaseGroup(p: JobPhase) {
  if (["warehouse_prep", "loading", "inspection"].includes(p.phase_type))
    return "Warehouse";
  if (["delivery", "return_transport"].includes(p.phase_type))
    return "Transport";
  if (["installation", "on_rent", "teardown"].includes(p.phase_type))
    return "Field Operations";
  return "Operations";
}
function requestedText(p: JobPhase) {
  if (p.required_window_start && p.required_window_end)
    return `${dt(p.required_window_start)} → ${dt(p.required_window_end)}`;
  if (p.required_window_start) return `Starts ${dt(p.required_window_start)}`;
  if (p.required_window_end) return `Complete by ${dt(p.required_window_end)}`;
  return "Dispatch sets the operational window.";
}
function visiblePhases(phases: JobPhase[]) {
  return phases.filter(
    (p) =>
      !["pickup", "receiving"].includes(p.phase_code) && p.status !== "skipped",
  );
}
function terminal(p: JobPhase) {
  return ["completed", "cancelled", "skipped"].includes(p.status);
}
function executionLabel(p: JobPhase) {
  if (p.status === "completed") return "Complete";
  if (p.status === "in_progress") return "In Progress";
  if (p.status === "blocked") return "Blocked";
  if (p.status === "cancelled" || p.status === "skipped") return "Not Required";
  if (p.status === "scheduled") return "Waiting";
  return "Not Started";
}
function executionClass(p: JobPhase) {
  if (p.status === "completed") return "status-completed";
  if (p.status === "in_progress") return "status-open";
  if (p.status === "blocked") return "status-blocking";
  return "";
}
type Tab = "overview" | "schedule" | "activity" | "attachments";

type RunAction = () => Promise<{
  data: unknown;
  error: { message: string } | null;
}>;
type Run = (action: RunAction, success: string) => Promise<void>;

export function OperationsJobDetailPage() {
  const { jobId } = useParams();
  const r = useRpcResource(
    () =>
      jobId
        ? operationsJobsApi.detail(jobId)
        : Promise.resolve({
            data: null,
            error: { message: "Job id is missing." },
          }),
    [jobId],
  );
  const staffR = useRpcResource(
    () =>
      jobId
        ? operationalCoreApi.staffing(jobId)
        : Promise.resolve({ data: null, error: null }),
    [jobId],
  );
  const transportR = useRpcResource(
    () =>
      jobId
        ? operationsJobsApi.transport(jobId)
        : Promise.resolve({ data: null, error: null }),
    [jobId],
  );
  const [tab, setTab] = useState<Tab>("overview");
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  async function run(action: RunAction, success: string) {
    setWorking(true);
    setMessage(null);
    setActionError(null);
    const x = await action();
    if (x.error) setActionError(x.error.message);
    else {
      setMessage(success);
      await Promise.all([r.reload(), staffR.reload(), transportR.reload()]);
    }
    setWorking(false);
  }
  if (r.loading) return <Loading />;
  if (r.error)
    return (
      <>
        <PageHeader title="Job & Dispatch" />
        <ErrorPanel message={r.error} />
      </>
    );
  if (!r.data) return <ErrorPanel message="Job detail did not return data." />;
  const data = r.data,
    job = data.job,
    phases = visiblePhases(data.phases),
    staffing = staffR.data;
  const attention = data.dispatch_gates.filter(
    (g) => g.blocking && !["passed", "waived", "resolved"].includes(g.status),
  ).length;
  const scheduledCount = phases.filter((p) =>
    isDispatchChecked(p, staffing),
  ).length;
  const dispatchLabel =
    phases.length > 0 && scheduledCount === phases.length
      ? "All Scheduled"
      : `${phases.length - scheduledCount} to schedule`;
  const completedCount = phases.filter(terminal).length;
  const activityLabel =
    job.status === "cancelled"
      ? "Cancelled"
      : phases.length > 0 && completedCount === phases.length
        ? "All Complete"
        : phases.some(
              (p) =>
                p.status === "in_progress" ||
                p.status === "completed" ||
                p.actual_start,
            )
          ? "In Progress"
          : "Not Started";
  return (
    <>
      <PageHeader
        title={job.job_name}
        subtitle={`${job.account_name ?? "Customer not assigned"} · Ref ${job.job_number} · ${job.site_name ?? "Site not named"}`}
        actions={
          <>
            <Link className="button-link" to="/operations/calendar">
              Dispatch Calendar
            </Link>
            <Link className="button-link" to="/operations/jobs">
              Back to Jobs
            </Link>
            <button
              onClick={() =>
                void Promise.all([
                  r.reload(),
                  staffR.reload(),
                  transportR.reload(),
                ])
              }
            >
              Refresh
            </button>
          </>
        }
      />
      {message && <div className="notice notice-success">{message}</div>}
      {actionError && <ErrorPanel message={actionError} />}
      <div className="metric-grid ops-metrics">
        <Metric label="Job status" value={label(job.status)} />
        <Metric label="Dispatch" value={dispatchLabel} />
        <Metric label="Activity" value={activityLabel} />
        <Metric label="Priority" value={label(job.priority)} />
        <Metric label="Needs attention" value={attention} />
        <Metric label="Phases" value={phases.length} />
      </div>
      <nav className="job-tabs" aria-label="Job workspace">
        <TabButton
          active={tab === "overview"}
          onClick={() => setTab("overview")}
        >
          Overview
        </TabButton>
        <TabButton
          active={tab === "schedule"}
          onClick={() => setTab("schedule")}
        >
          Schedule & Crews
        </TabButton>
        <TabButton
          active={tab === "activity"}
          onClick={() => setTab("activity")}
        >
          Activity
        </TabButton>
        <TabButton
          active={tab === "attachments"}
          onClick={() => setTab("attachments")}
        >
          Attachments
        </TabButton>
      </nav>
      {tab === "overview" && (
        <Overview data={data} working={working} run={run} />
      )}{" "}
      {tab === "schedule" &&
        (staffR.loading ? (
          <Loading />
        ) : staffR.error ? (
          <ErrorPanel message={staffR.error} />
        ) : staffing ? (
          <>
            <>
              {transportR.error && (
                <div className="notice notice-warning">
                  Transport controls could not load: {transportR.error}
                </div>
              )}
            </>
            <ScheduleCrewTab
              jobId={job.id}
              phases={phases}
              staffing={staffing}
              transport={transportR.data}
              canManage={data.capabilities.manage}
              working={working}
              run={run}
            />
          </>
        ) : (
          <ErrorPanel message="Crew staffing data did not return." />
        ))}{" "}
      {tab === "activity" && <Activity phases={phases} staffing={staffing} />}
      {tab === "attachments" && <JobSharedAttachments jobId={job.id} />}
    </>
  );
}
function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button className={active ? "active" : ""} onClick={onClick}>
      {children}
    </button>
  );
}
function isDispatchChecked(
  p: JobPhase,
  staffing: StaffingWorkspace | null | undefined,
) {
  if (terminal(p)) return true;
  const timed = Boolean(p.scheduled_start && p.scheduled_end);
  if (!timed) return false;
  if (p.phase_type === "on_rent") return true;
  if (!staffing) return false;
  return (
    staffing.crew_assignments.some(
      (a) => a.phase_id === p.id && a.status === "assigned",
    ) ||
    staffing.employee_assignments.some(
      (a) => a.phase_id === p.id && a.status === "assigned",
    )
  );
}
function assignedCrews(
  p: JobPhase,
  staffing: StaffingWorkspace | null | undefined,
) {
  return (
    staffing?.crew_assignments.filter(
      (a) =>
        a.phase_id === p.id && ["assigned", "completed"].includes(a.status),
    ) ?? []
  );
}

function Overview({
  data,
  working,
  run,
}: {
  data: OperationsJobDetail;
  working: boolean;
  run: Run;
}) {
  const job = data.job;
  return (
    <div className="detail-grid operations-job-grid">
      <div>
        <Card title="Job & site">
          <dl className="detail-list">
            <dt>Job name</dt>
            <dd>{job.job_name}</dd>
            <dt>Reference</dt>
            <dd>{job.job_number}</dd>
            <dt>Customer</dt>
            <dd>{job.account_name ?? "—"}</dd>
            <dt>Fulfillment</dt>
            <dd>{job.fulfillment_location ?? "—"}</dd>
            <dt>Install / ship-to site</dt>
            <dd>{job.site_name ?? "—"}</dd>
            <dt>Address</dt>
            <dd>
              {[
                job.site_address_line1,
                job.site_city,
                job.site_state_region,
                job.site_postal_code,
              ]
                .filter(Boolean)
                .join(", ") || "—"}
            </dd>
            <dt>Site contact</dt>
            <dd>
              {job.site_contact_name ?? "—"}
              {job.site_contact_phone ? ` · ${job.site_contact_phone}` : ""}
            </dd>
            <dt>Rental</dt>
            <dd>
              {String(job.rental_start_date ?? "—")} →{" "}
              {String(job.rental_end_date ?? "—")}
            </dd>
            <dt>Delivery requested</dt>
            <dd>{dt(job.delivery_window_start)}</dd>
            <dt>Install requested</dt>
            <dd>{dt(job.install_window_start)}</dd>
            <dt>Pickup / teardown requested</dt>
            <dd>{dt(job.pickup_window_start)}</dd>
          </dl>
        </Card>
        <Card title="Order scope / What this Job includes">
          {data.order_items.length === 0 ? (
            <div className="empty-state">
              No Order items are attached to this Job.
            </div>
          ) : (
            <div className="job-order-scope">
              {data.order_items.map((item) => (
                <OrderScopeItem item={item} key={item.line_key} />
              ))}
            </div>
          )}
        </Card>
      </div>
      <ActionPanel data={data} working={working} run={run} />
    </div>
  );
}
function OrderScopeItem({
  item,
}: {
  item: OperationsJobDetail["order_items"][number];
}) {
  const cfg = item.configuration_snapshot ?? {};
  const width = Number(cfg.width_m ?? 0);
  const length = Number(cfg.length_m ?? 0);
  const bays = Number(cfg.bay_count ?? 0);
  const actual = item.bom_components ?? [];
  const reference = item.bom_reference_components ?? [];
  const parts = actual.length ? actual : reference;
  const isReference = actual.length === 0 && reference.length > 0;
  const totalQty = (q: number | null | undefined) =>
    Number(q ?? 0) * Number(item.quantity || 1);
  const grouped = reference.reduce<Record<string, typeof reference>>((a, p) => {
    const key = p.category_name ?? "Other";
    (a[key] ??= []).push(p);
    return a;
  }, {});
  return (
    <div className="job-order-scope-item">
      <div className="job-order-scope-row">
        <div>
          <span className="eyebrow">
            {label(item.line_type)}
            {item.sku ? ` · ${item.sku}` : ""}
          </span>
          <strong>{item.product_name ?? item.description}</strong>
          {item.description && item.description !== item.product_name && (
            <small>{item.description}</small>
          )}
          {width > 0 && length > 0 && (
            <small>
              {Math.round(width / 0.3048)}' × {Math.round(length / 0.3048)}'
              {bays > 0 ? ` · ${bays} bays` : ""}
            </small>
          )}
          {item.notes && <small>{item.notes}</small>}
        </div>
        <strong>Qty {Number(item.quantity).toLocaleString()}</strong>
      </div>
      {parts.length > 0 && (
        <details className="job-bom-details">
          <summary>
            <span>
              <strong>View tent BOM / assembly contents</strong>
              <small>
                {isReference
                  ? `${parts.length} draft component records`
                  : `${parts.length} component${parts.length === 1 ? "" : "s"}`}
              </small>
            </span>
          </summary>
          <div className="job-bom-body">
            {isReference ? (
              <>
                <div className="job-bom-note">
                  <strong>Draft reference components</strong>
                  <span>
                    These component shells show the tent assembly categories we
                    have loaded. Exact Mahaffey / Point of Rental quantities are
                    not linked yet, so they do not create inventory demand or
                    change availability.
                  </span>
                </div>
                {Object.entries(grouped).map(([group, items]) => (
                  <section className="job-bom-group" key={group}>
                    <h4>{group}</h4>
                    {items.map((p) => (
                      <div className="job-bom-row" key={p.product_id}>
                        <div>
                          <strong>{p.name}</strong>
                          <small>{p.sku ?? "No SKU yet"}</small>
                        </div>
                        <span>Qty pending POR</span>
                      </div>
                    ))}
                  </section>
                ))}
              </>
            ) : (
              <>
                {actual.map((p) => (
                  <div
                    className="job-bom-row"
                    key={`${p.product_id}-${p.component_role ?? ""}`}
                  >
                    <div>
                      <strong>{p.name}</strong>
                      <small>
                        {[
                          p.sku,
                          p.component_role ? label(p.component_role) : null,
                          p.is_optional ? "optional" : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </small>
                    </div>
                    <span>
                      Qty{" "}
                      {totalQty(p.required_quantity_per_unit).toLocaleString(
                        undefined,
                        { maximumFractionDigits: 2 },
                      )}
                      {p.unit_of_measure ? ` ${p.unit_of_measure}` : ""}
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>
        </details>
      )}
    </div>
  );
}
function ActionPanel({
  data,
  working,
  run,
}: {
  data: OperationsJobDetail;
  working: boolean;
  run: Run;
}) {
  const j = data.job;
  const [status, setStatus] = useState(data.allowed_job_transitions[0] ?? "");
  const [reason, setReason] = useState("");
  const [priority, setPriority] = useState(j.priority ?? "normal");
  const [siteAccess, setSiteAccess] = useState(j.site_access_notes ?? "");
  const [install, setInstall] = useState(j.install_requirements ?? "");
  const [safety, setSafety] = useState(j.safety_notes ?? "");
  const manage = data.capabilities.manage;
  return (
    <aside className="action-drawer operations-actions">
      <h2>Dispatch controls</h2>
      <p className="muted">
        Exact phase times and crews are handled in Schedule & Crews. Shared Work
        Order and customer files are kept in the Job Attachments workspace.
      </p>
      {manage && ["planning", "on_hold"].includes(j.status) && (
        <button
          className="primary full"
          disabled={working}
          onClick={() =>
            void run(
              () => operationsJobsApi.release(j.id),
              "Job readiness checked and released to Dispatch.",
            )
          }
        >
          Check readiness & release to Dispatch
        </button>
      )}
      <div className="action-form">
        <strong>Job status</strong>
        <select
          disabled={
            !manage || working || data.allowed_job_transitions.length === 0
          }
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {data.allowed_job_transitions.length === 0 ? (
            <option value="">No available transition</option>
          ) : (
            data.allowed_job_transitions.map((s) => (
              <option value={s} key={s}>
                {label(s)}
              </option>
            ))
          )}
        </select>
        <input
          disabled={!manage || working}
          placeholder="Reason / note"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <button
          disabled={!manage || working || !status}
          onClick={() =>
            void run(
              () =>
                operationsJobsApi.transitionJob(j.id, status, reason || null),
              `Job moved to ${label(status)}.`,
            )
          }
        >
          Apply status
        </button>
      </div>
      <div className="action-form">
        <strong>Operational details</strong>
        <label className="field">
          <span>Priority</span>
          <select
            disabled={!manage || working}
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          >
            {["low", "normal", "high", "urgent", "emergency"].map((x) => (
              <option key={x} value={x}>
                {label(x)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Site access</span>
          <textarea
            disabled={!manage || working}
            value={siteAccess}
            onChange={(e) => setSiteAccess(e.target.value)}
          />
        </label>
        <label className="field">
          <span>Install requirements</span>
          <textarea
            disabled={!manage || working}
            value={install}
            onChange={(e) => setInstall(e.target.value)}
          />
        </label>
        <label className="field">
          <span>Safety notes</span>
          <textarea
            disabled={!manage || working}
            value={safety}
            onChange={(e) => setSafety(e.target.value)}
          />
        </label>
        <button
          disabled={!manage || working}
          onClick={() =>
            void run(
              () =>
                operationsJobsApi.updateDetails(
                  j.id,
                  siteAccess || null,
                  install || null,
                  safety || null,
                  priority,
                ),
              "Operational details updated.",
            )
          }
        >
          Save details
        </button>
      </div>
    </aside>
  );
}

function ScheduleCrewTab({
  jobId,
  phases,
  staffing,
  transport,
  canManage,
  working,
  run,
}: {
  jobId: string;
  phases: JobPhase[];
  staffing: StaffingWorkspace;
  transport: DispatchJobTransportWorkspace | null;
  canManage: boolean;
  working: boolean;
  run: Run;
}) {
  const checked = phases.filter((p) => isDispatchChecked(p, staffing)).length;
  return (
    <Card title="Dispatch Checklist">
      <div className="ops-checklist-heading">
        <div>
          <strong>
            {checked} of {phases.length} phases scheduled
          </strong>
          <p className="muted">
            Set the time and assigned crew for each phase. Delivery and Return
            Transport can use one or more available Fleet trucks, plus qualified
            drivers from the assigned crews. Shared Work Order and customer
            files are kept in the Job Attachments workspace.
          </p>
        </div>
        <span
          className={`status-pill ${checked === phases.length ? "status-passed" : "status-open"}`}
        >
          {checked === phases.length ? "All Scheduled" : "Scheduling"}
        </span>
      </div>
      <div className="ops-phase-grid">
        {phases.map((p) => (
          <PhaseScheduler
            key={p.id}
            jobId={jobId}
            phase={p}
            staffing={staffing}
            transportLeg={transport?.legs.find((l) => l.phase_id === p.id)}
            transportManage={Boolean(transport?.capabilities.manage)}
            canManage={canManage}
            working={working}
            run={run}
          />
        ))}
      </div>
    </Card>
  );
}
function PhaseScheduler({
  jobId,
  phase,
  staffing,
  transportLeg,
  transportManage,
  canManage,
  working,
  run,
}: {
  jobId: string;
  phase: JobPhase;
  staffing: StaffingWorkspace;
  transportLeg: DispatchTransportLeg | undefined;
  transportManage: boolean;
  canManage: boolean;
  working: boolean;
  run: Run;
}) {
  const [start, setStart] = useState(seedStart(phase));
  const [end, setEnd] = useState(seedEnd(phase));
  const [crewId, setCrewId] = useState("");
  const [availability, setAvailability] = useState<CrewAvailabilityItem[]>([]);
  const [availabilityError, setAvailabilityError] = useState<string | null>(
    null,
  );
  const [checking, setChecking] = useState(false);
  const assignments = useMemo(
    () => assignedCrews(phase, staffing),
    [staffing.crew_assignments, phase.id],
  );
  const assignedIds = new Set(assignments.map((a) => a.crew_id));
  useEffect(() => {
    let live = true;
    if (!start || !end || new Date(end) <= new Date(start)) {
      setAvailability([]);
      setAvailabilityError(null);
      setChecking(false);
      return () => {
        live = false;
      };
    }
    setChecking(true);
    setAvailabilityError(null);
    operationalCoreApi
      .crewAvailability(
        jobId,
        phase.id,
        new Date(start).toISOString(),
        new Date(end).toISOString(),
      )
      .then((x) => {
        if (!live) return;
        if (x.error) {
          setAvailability([]);
          setAvailabilityError(x.error.message);
        } else setAvailability(x.data?.crews ?? []);
        setChecking(false);
      });
    return () => {
      live = false;
    };
  }, [jobId, phase.id, start, end, staffing.crew_assignments.length]);
  const selectable = availability.filter((c) => !assignedIds.has(c.id));
  const selected = availability.find((c) => c.id === crewId);
  const valid = Boolean(start && end && new Date(end) > new Date(start));
  const checked = isDispatchChecked(phase, staffing);
  const editable = canManage && !working && !terminal(phase);
  async function saveAndAssign() {
    if (!crewId || !valid) return;
    await run(
      async () => {
        const scheduled = await operationsJobsApi.schedulePhase(
          phase.id,
          new Date(start).toISOString(),
          new Date(end).toISOString(),
        );
        if (scheduled.error) return scheduled;
        return operationalCoreApi.assignCrew(
          jobId,
          phase.id,
          crewId,
          new Date(start).toISOString(),
          new Date(end).toISOString(),
          assignments.length === 0,
        );
      },
      `${selected?.name ?? "Crew"} assigned to ${phaseLabel(phase)}.`,
    );
    setCrewId("");
  }
  return (
    <section className={`ops-phase-card ${checked ? "ready" : ""}`}>
      <div className="ops-phase-head">
        <span className={`ops-check ${checked ? "done" : ""}`}>
          {checked ? "✓" : ""}
        </span>
        <div className="ops-phase-title">
          <h3>{phaseLabel(phase)}</h3>
          <span>{phaseGroup(phase)}</span>
          <small>Requested: {requestedText(phase)}</small>
          {transportLeg && (
            <small>
              Logistics route: {transportLeg.route_number} ·{" "}
              {label(transportLeg.route_status)}
            </small>
          )}
        </div>
        <span
          className={`status-pill ${checked ? "status-passed" : "status-open"}`}
        >
          {checked ? "Scheduled" : "Needs setup"}
        </span>
      </div>
      <div className="ops-time-grid">
        <label className="field">
          <span>Start</span>
          <input
            type="datetime-local"
            disabled={!editable}
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </label>
        <label className="field">
          <span>End</span>
          <input
            type="datetime-local"
            disabled={!editable}
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />
        </label>
        <button
          disabled={!editable || !valid}
          onClick={() =>
            void run(
              () =>
                operationsJobsApi.schedulePhase(
                  phase.id,
                  new Date(start).toISOString(),
                  new Date(end).toISOString(),
                ),
              `${phaseLabel(phase)} time saved.`,
            )
          }
        >
          Save time
        </button>
      </div>
      {transportLeg && (
        <TransportTruckControl
          leg={transportLeg}
          canManage={transportManage && editable}
          working={working}
          run={run}
        />
      )}
      <div className="ops-crews">
        <strong>
          {phase.phase_type === "on_rent"
            ? "Crew coverage (optional)"
            : "Assigned crew"}
        </strong>
        {assignments.length === 0 ? (
          <small className="ops-none">
            {phase.phase_type === "on_rent"
              ? "No dedicated on-rent crew required."
              : "No crew assigned yet."}
          </small>
        ) : (
          <div className="ops-crew-list">
            {assignments.map((a) => (
              <CrewAssignmentCard
                key={a.id}
                assignment={a}
                transportLeg={transportLeg}
                transportManage={transportManage}
                editable={editable}
                staffingManage={staffing.capabilities.manage}
                working={working}
                run={run}
              />
            ))}
          </div>
        )}
        <div className="ops-add-crew">
          <select
            disabled={
              !editable || !staffing.capabilities.assign || !valid || checking
            }
            value={crewId}
            onChange={(e) => setCrewId(e.target.value)}
          >
            <option value="">
              {checking ? "Checking availability…" : "Select available crew"}
            </option>
            {selectable.map((c) => (
              <option key={c.id} value={c.id} disabled={!c.available}>
                {c.recommended ? "★ " : ""}
                {c.name} · {label(c.crew_type)} · {c.member_count} members
                {c.available ? "" : ` · ${c.conflict_reason ?? "Unavailable"}`}
              </option>
            ))}
          </select>
          <button
            className="primary"
            disabled={!editable || !crewId || !selected?.available || !valid}
            onClick={() => void saveAndAssign()}
          >
            Add crew to phase
          </button>
        </div>
        {availabilityError && (
          <div className="notice notice-warning">{availabilityError}</div>
        )}
      </div>
    </section>
  );
}

function TransportTruckControl({
  leg,
  canManage,
  working,
  run,
}: {
  leg: DispatchTransportLeg;
  canManage: boolean;
  working: boolean;
  run: Run;
}) {
  const vehicles = leg.vehicles ?? (leg.vehicle ? [leg.vehicle] : []);
  const [resourceId, setResourceId] = useState("");
  useEffect(() => setResourceId(""), [vehicles.length]);
  const options = leg.vehicle_options.filter((v) => !v.assigned_here);
  const selected = options.find((v) => v.resource_id === resourceId);
  const routeLocked = [
    "dispatched",
    "in_progress",
    "completed",
    "cancelled",
  ].includes(leg.route_status);
  const assignable = canManage && !working && !routeLocked;
  const driverCount = leg.crew_drivers.filter((c) => Boolean(c.driver)).length;
  const driversNeeded = Math.max(vehicles.length - driverCount, 0);
  return (
    <div className="ops-driver-control">
      <div className="ops-driver-current">
        <span>Route trucks</span>
        <strong>{vehicles.length} assigned</strong>
        <small>
          {driverCount} driver{driverCount === 1 ? "" : "s"} assigned
        </small>
      </div>
      {vehicles.length > 0 && (
        <div className="ops-crew-list">
          {vehicles.map((v) => (
            <div className="ops-crew-chip" key={v.resource_id}>
              <span>
                <strong>{v.name}</strong>
                <small>
                  {[
                    v.vehicle_number,
                    v.vehicle_type ? label(v.vehicle_type) : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </small>
              </span>
              <button
                disabled={!assignable}
                onClick={() =>
                  void run(
                    () =>
                      operationsJobsApi.releaseTransportTruck(
                        leg.route_id,
                        v.resource_id,
                      ),
                    `${v.name} released from this route.`,
                  )
                }
              >
                Release
              </button>
            </div>
          ))}
        </div>
      )}
      {options.length > 0 ? (
        <div className="ops-driver-actions">
          <select
            disabled={!assignable}
            value={resourceId}
            onChange={(e) => setResourceId(e.target.value)}
          >
            <option value="">Add another available truck</option>
            {options.map((v) => (
              <option
                key={v.resource_id}
                value={v.resource_id}
                disabled={!v.available}
              >
                {v.name}
                {v.vehicle_number ? ` · ${v.vehicle_number}` : ""}
                {v.vehicle_type ? ` · ${label(v.vehicle_type)}` : ""}
                {v.available ? "" : ` · ${v.conflict_reason ?? "Unavailable"}`}
              </option>
            ))}
          </select>
          <button
            disabled={!assignable || !resourceId || !selected?.available}
            onClick={() =>
              void run(
                () =>
                  operationsJobsApi.assignTransportTruck(
                    leg.route_id,
                    resourceId,
                  ),
                `${selected?.name ?? "Truck"} added to ${leg.route_type === "delivery" ? "Delivery" : "Return Transport"}.`,
              )
            }
          >
            Add truck
          </button>
        </div>
      ) : vehicles.length === 0 ? (
        <small className="ops-driver-empty">
          No active Fleet trucks are available. Add trucks under{" "}
          <Link to="/logistics/fleet">Fleet & Drivers</Link>.
        </small>
      ) : (
        <small className="ops-driver-empty">
          No additional Fleet trucks are available for this time window.
        </small>
      )}
      {driversNeeded > 0 && (
        <div className="notice notice-warning">
          Assign {driversNeeded} more qualified driver
          {driversNeeded === 1 ? "" : "s"} before departure. A route needs at
          least one assigned driver per truck.
        </div>
      )}
      {leg.vehicle_options.some((v) => !v.available && !v.assigned_here) && (
        <small className="ops-driver-empty">
          Trucks already committed to another route or task during this time are
          unavailable and cannot be double-booked.
        </small>
      )}
      {routeLocked && (
        <small className="ops-driver-empty">
          Truck assignment is locked because this route has already departed.
        </small>
      )}
    </div>
  );
}

function CrewAssignmentCard({
  assignment,
  transportLeg,
  transportManage,
  editable,
  staffingManage,
  working,
  run,
}: {
  assignment: CrewAssignment;
  transportLeg: DispatchTransportLeg | undefined;
  transportManage: boolean;
  editable: boolean;
  staffingManage: boolean;
  working: boolean;
  run: Run;
}) {
  const crewDriver = transportLeg?.crew_drivers.find(
    (c) => c.crew_assignment_id === assignment.id,
  );
  return (
    <div className="ops-crew-assignment">
      <div className="ops-crew-chip">
        <span>
          <strong>{assignment.crew_name}</strong>
          <small>
            {dt(assignment.starts_at)} → {dt(assignment.ends_at)}
          </small>
        </span>
        {assignment.status === "assigned" && (
          <button
            disabled={!editable || !staffingManage}
            onClick={() =>
              void run(
                () =>
                  operationalCoreApi.releaseCrew(
                    assignment.id,
                    "Released from Dispatch checklist",
                  ),
                "Crew released from this phase.",
              )
            }
          >
            Release
          </button>
        )}
      </div>
      {transportLeg && crewDriver && (
        <TransportCrewDriverControl
          leg={transportLeg}
          crew={crewDriver}
          canManage={transportManage && editable}
          working={working}
          run={run}
        />
      )}
    </div>
  );
}

function TransportCrewDriverControl({
  leg,
  crew,
  canManage,
  working,
  run,
}: {
  leg: DispatchTransportLeg;
  crew: DispatchTransportCrewDriver;
  canManage: boolean;
  working: boolean;
  run: Run;
}) {
  const current = crew.driver;
  const [driverId, setDriverId] = useState(current?.employee_id ?? "");
  useEffect(
    () => setDriverId(current?.employee_id ?? ""),
    [current?.employee_id],
  );
  const options = crew.driver_options;
  const selected = options.find((o) => o.employee_id === driverId);
  const routeLocked = [
    "dispatched",
    "in_progress",
    "completed",
    "cancelled",
  ].includes(leg.route_status);
  const assignable = canManage && !working && !routeLocked;
  return (
    <div className="ops-driver-control">
      <div className="ops-driver-current">
        <span>Route driver</span>
        <strong>{current?.employee_name ?? "Not assigned"}</strong>
        {current && <small>{label(current.driver_role)}</small>}
      </div>
      {options.length > 0 ? (
        <div className="ops-driver-actions">
          <select
            disabled={!assignable}
            value={driverId}
            onChange={(e) => setDriverId(e.target.value)}
          >
            <option value="">Select driver from {crew.crew_name}</option>
            {options.map((o) => (
              <option
                key={o.employee_id}
                value={o.employee_id}
                disabled={
                  !o.available && o.employee_id !== current?.employee_id
                }
              >
                {o.employee_name}
                {o.employee_number ? ` · ${o.employee_number}` : ""}
                {o.available ? "" : ` · ${o.conflict_reason ?? "Unavailable"}`}
              </option>
            ))}
          </select>
          <button
            disabled={
              !assignable ||
              !driverId ||
              driverId === current?.employee_id ||
              !selected?.available
            }
            onClick={() =>
              void run(
                () =>
                  operationsJobsApi.assignTransportCrewDriver(
                    leg.route_id,
                    crew.crew_assignment_id,
                    driverId,
                  ),
                `${selected?.employee_name ?? "Driver"} assigned to ${crew.crew_name} for ${leg.route_type === "delivery" ? "Delivery" : "Return Transport"}.`,
              )
            }
          >
            Assign driver
          </button>
          {current && (
            <button
              disabled={!assignable}
              onClick={() =>
                void run(
                  () =>
                    operationsJobsApi.releaseTransportCrewDriver(
                      leg.route_id,
                      crew.crew_assignment_id,
                    ),
                  `Driver released from ${crew.crew_name} route assignment.`,
                )
              }
            >
              Release driver
            </button>
          )}
        </div>
      ) : (
        <small className="ops-driver-empty">
          No active qualified Fleet driver profile is available in this crew.
          Add/activate a driver under{" "}
          <Link to="/logistics/fleet">Fleet & Drivers</Link>.
        </small>
      )}
      {routeLocked && (
        <small className="ops-driver-empty">
          Driver assignment is locked because this route has already departed.
        </small>
      )}
    </div>
  );
}

function Activity({
  phases,
  staffing,
}: {
  phases: JobPhase[];
  staffing: StaffingWorkspace | null | undefined;
}) {
  const complete = phases.filter(terminal).length;
  return (
    <Card title="Job Activity Checklist">
      <div className="ops-checklist-heading">
        <div>
          <strong>
            {complete} of {phases.length} phases complete
          </strong>
          <p className="muted">
            This is the execution view Dispatch tracks after scheduling. Field,
            Warehouse and Transport activity updates the checklist as each phase
            is performed—no raw system event log.
          </p>
        </div>
        <span
          className={`status-pill ${complete === phases.length ? "status-passed" : "status-open"}`}
        >
          {complete === phases.length ? "All Complete" : "Work Tracking"}
        </span>
      </div>
      <div className="ops-activity-list">
        {phases.map((p) => {
          const crews = assignedCrews(p, staffing);
          const scheduled = Boolean(p.scheduled_start && p.scheduled_end);
          return (
            <div
              className={`ops-activity-row ${p.status === "completed" ? "complete" : ""}`}
              key={p.id}
            >
              <span className={`ops-check ${terminal(p) ? "done" : ""}`}>
                {terminal(p) ? "✓" : ""}
              </span>
              <div className="ops-activity-main">
                <strong>{phaseLabel(p)}</strong>
                <small>
                  {scheduled
                    ? `${dt(p.scheduled_start)} → ${dt(p.scheduled_end)}`
                    : "No Dispatch time set"}
                </small>
                <small>
                  {crews.length
                    ? crews.map((c) => c.crew_name).join(", ")
                    : p.phase_type === "on_rent"
                      ? "No dedicated crew required"
                      : "Crew not assigned"}
                </small>
              </div>
              <div className="ops-activity-state">
                <span>Dispatch</span>
                <strong>
                  {isDispatchChecked(p, staffing)
                    ? "Scheduled"
                    : "Needs schedule"}
                </strong>
              </div>
              <div className="ops-activity-state">
                <span>Operations</span>
                <strong className={`status-pill ${executionClass(p)}`}>
                  {executionLabel(p)}
                </strong>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
